import React, { useState } from 'react'

import { TeamService } from '../../../../shared/services/teamService'
import {
  XMarkIcon,
  UserPlusIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'

interface InviteTeamMemberModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  teamId: string
  teamName: string
}

const InviteTeamMemberModal: React.FC<InviteTeamMemberModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  teamId,
  teamName,
}) => {
  const { t } = useTranslation()
  const [formData, setFormData] = useState({
    email: '',
    role: 'viewer' as 'editor' | 'viewer',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  if (!isOpen) {
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.email.trim()) {
      setError(t('team.emailRequired'))
      return
    }

    // 이메일 형식 검증
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email)) {
      setError(t('team.emailInvalid'))
      return
    }

    setLoading(true)
    setError('')

    try {
      const data = await TeamService.inviteTeamMember(teamId, formData)

      if (data.success) {
        // 이메일 발송 상태 확인 및 사용자에게 피드백
        if (data.emailSent) {
          setSuccess(true)
          setTimeout(() => {
            onSuccess()
            handleClose()
          }, 2000)
        } else {
          // 이메일 발송 실패 시 경고 표시
          const warningMessage = `초대가 생성되었지만 이메일 발송에 실패했습니다.\n초대 목록에서 재발송할 수 있습니다.\n\n오류: ${data.emailError || t('team.unknown')}`
          toast(warningMessage)
          onSuccess() // 초대 목록 갱신
          handleClose()
        }
      } else {
        setError(data.message || t('team.inviteError'))
      }
    } catch (error) {
      console.error('팀원 초대 실패:', error)
      setError(t('team.networkError'))
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setFormData({ email: '', role: 'viewer' })
    setError('')
    setSuccess(false)
    onClose()
  }

  if (success) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="mx-4 w-full max-w-md rounded-xl bg-white p-8 text-center shadow-2xl">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
            <CheckCircleIcon className="h-8 w-8 text-green-600" />
          </div>
          <h3 className="mb-2 text-lg font-semibold text-gray-900">
            {t('team.inviteSent')}
          </h3>
          <p className="mb-4 text-sm text-gray-600">
            <span className="font-medium">{formData.email}</span>에게 팀 초대
            이메일을 발송했습니다.
          </p>
          <p className="text-xs text-gray-500">잠시 후 자동으로 닫힙니다...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="mx-4 w-full max-w-md transform rounded-xl bg-white shadow-2xl transition-all">
        {/* 헤더 */}
        <div className="flex items-center justify-between border-b border-gray-200 p-6">
          <div className="flex items-center">
            <div className="mr-3 flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
              <UserPlusIcon className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">팀원 초대</h2>
              <p className="text-sm text-gray-500">{teamName}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 transition-colors hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* 폼 */}
        <form onSubmit={handleSubmit} className="space-y-5 p-6">
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* 이메일 */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              이메일 주소 *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={e =>
                setFormData({ ...formData, email: e.target.value })
              }
              placeholder="teammate@example.com"
              className="w-full rounded-lg border border-gray-300 px-4 py-3 transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              {t('team.inviteDescription')}
            </p>
          </div>

          {/* 역할 선택 */}
          <div>
            <label className="mb-3 block text-sm font-medium text-gray-700">
              {t('team.roleSelect')}
            </label>
            <div className="space-y-3">
              <label className="flex cursor-pointer items-start">
                <input
                  type="radio"
                  name="role"
                  value="viewer"
                  checked={formData.role === 'viewer'}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      role: e.target.value as 'viewer',
                    })
                  }
                  className="mr-3 mt-1 text-blue-600 focus:ring-blue-500"
                />
                <div>
                  <div className="font-medium text-gray-900">뷰어</div>
                  <div className="text-sm text-gray-500">
                    공유된 북마크와 폴더를 볼 수 있습니다
                  </div>
                </div>
              </label>
              <label className="flex cursor-pointer items-start">
                <input
                  type="radio"
                  name="role"
                  value="editor"
                  checked={formData.role === 'editor'}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      role: e.target.value as 'editor',
                    })
                  }
                  className="mr-3 mt-1 text-blue-600 focus:ring-blue-500"
                />
                <div>
                  <div className="font-medium text-gray-900">편집자</div>
                  <div className="text-sm text-gray-500">
                    북마크와 폴더를 보고 편집할 수 있으며, 다른 팀원을 초대할 수
                    있습니다
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* 안내 메시지 */}
          <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg
                  className="mt-0.5 h-5 w-5 text-yellow-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h4 className="text-sm font-medium text-yellow-900">
                  {t('team.inviteNoteTitle')}
                </h4>
                <p className="mt-1 text-xs text-yellow-700">
                  {t('team.inviteNote')}
                </p>
              </div>
            </div>
          </div>

          {/* 버튼들 */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading || !formData.email.trim()}
              className="transform rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-2 text-sm font-medium text-white transition-all hover:scale-105 hover:from-blue-700 hover:to-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? (
                <div className="flex items-center">
                  <svg
                    className="-ml-1 mr-2 h-4 w-4 animate-spin text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  {t('team.inviteSending')}
                </div>
              ) : (
                t('team.inviteSubmit')
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default InviteTeamMemberModal
